import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from 'react-redux';
import { notification, Button, Space } from "antd";
import { FormOutlined } from "@ant-design/icons";
import { hasPrivilege } from "../../api/token.js";
import service from "../../services/Service.js";
import dayjs from "dayjs";
import { withLocale } from "../../l10n/index.jsx";
import DataTable from "../DataTable/index.js";
import DeleteButton from "../DeleteButton/DeleteButton.jsx";
import FilterColumns from "../FilterColumns/index.js";
import withNavigation from '../hooks/withNavigation.jsx';
import Loading from "../../utils/loading.jsx";
import Excelexport from "../../utils/excelexport.jsx";
import { GetColumnLiteralSearchProps } from '../../utils/functions.jsx';
import { formatDate, compareDates } from '../../utils/dates';
import { useGetModelsLookupQuery, useGetVersionsLookupQuery,
         useGetTenantsLookupQuery, invalidateRoutersLookup } from '../../services/lookupApi.js';

const EMPTY_ARRAY = [];
const COLUMNS_STORAGE_KEY = 'routerColumns_visible';

function RouterList({ lang, navigate }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: true,
    selectedColumns: null,
  });

  const [data, setData] = useState({
    raw: [],
    selectedId: '',
    filteredRows: [],
  });

  // ===== 2. SELECTORES / CONSTANTES =====
  const dispatch = useDispatch();
  const { data: models = EMPTY_ARRAY } = useGetModelsLookupQuery();
  const { data: versions = EMPTY_ARRAY } = useGetVersionsLookupQuery();
  const { data: tenants = EMPTY_ARRAY } = useGetTenantsLookupQuery();

  // ===== 3. DERIVADOS =====
  const modelFilter = useMemo(
    () => models
      .filter(model => model.device_type === 'router')
      .map(model => ({ text: model.name, value: model.uuid })),
    [models]
  );

  const versionFilter = useMemo(
    () => versions
      .filter(version => version.device_type === 'router')
      .map(version => ({ text: version.name, value: version.uuid })),
    [versions]
  );

  const tenantsFilter = useMemo(() => {
    const list = tenants.map(tenant => ({ text: tenant.name, value: tenant.uuid }));
    list.push({ text: lang.handle("Sin organización"), value: null });
    return list;
  }, [tenants, lang]);

  // ===== 4. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    updateUI('loading', true);
    try {
      const res = await service.get('routers');

      if (!res || res.data.ok === false) {
        notification["error"]({ message: lang.handle(res?.data?.message || 'Error al obtener datos') });
        updateData('raw', []);
        return;
      }

      updateData('raw', res.data.data || []);
    } catch (err) {
      console.error('Error devuelto: ' + err);
      notification["error"]({ message: lang.handle('Error al obtener datos') });
    } finally {
      updateUI('loading', false);
    }
  }, [lang]);

  /**
   * Enriquecer los datos crudos con información derivada o formateada para su visualización.
   */
  const enrichedData = useMemo(() => {
    return data.raw.map((element) => ({
      ...element,
      dateFormated: formatDate(element.date) || '-',
      tenant_name: tenants.find(tenant => tenant.uuid === element.tenant_uuid)?.name
        ?? lang.handle("Sin organización"),
      vpnLabel: element.vpn ? lang.handle('Sí') : lang.handle('No'),
    }));
  }, [data.raw, tenants, lang]);


  // ===== 6. HANDLERS =====
  const onDelete = async () => {
    updateUI('loading', true);
    try {
      const res = await service.del('routers/' + data.selectedId);

      if (!res || res.data.ok === false) {
        notification["error"]({ message: lang.handle(res?.data?.message) });
      } else {
        notification["success"]({ message: lang.handle('Router borrado correctamente') });
        dispatch(invalidateRoutersLookup());
      }
      await Pfetch();
    } catch (err) {
      console.error('Error devuelto: ' + err);
    } finally {
      updateData('selectedId', '');
      updateUI('loading', false);
    }
  };

  const onEdit = () => navigate(`/boards/edit/4/${data.selectedId}`);

  const onRowSelectionChange = (selectedRowKeys, selectedRows) => {
    updateData('selectedId', selectedRows[0]?.uuid || '');
  };

  const handleTableChange = useCallback((pagination, filters, sorter, extra) => {
    updateData('filteredRows', extra.currentDataSource);
  }, []);

  // ===== 7. EFFECTS =====
  useEffect(() => {
    Pfetch();
  }, [Pfetch]);

  useEffect(() => {
    const saved = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        updateUI('selectedColumns', parsed);
      }
    } catch {
      localStorage.removeItem(COLUMNS_STORAGE_KEY);
    }
  }, []);

  // ===== 8. CONFIG =====
  const allColumns = useMemo(() => [
    {
      title: lang.handle('Pedido'),
      dataIndex: 'order_id',
      ...GetColumnLiteralSearchProps('order_id', lang),
    },
    {
      title: 'UUID',
      dataIndex: 'uuid',
      ...GetColumnLiteralSearchProps('uuid', lang),
    },
    {
      title: 'IMEI',
      dataIndex: 'modem_imei',
      ...GetColumnLiteralSearchProps('modem_imei', lang),
    },
    {
      title: lang.handle('Tipo Teltonika'),
      dataIndex: 'modem_type_code',
      ...GetColumnLiteralSearchProps('modem_type_code', lang),
    },
    {
      title: lang.handle('Número de serie'),
      dataIndex: 'modem_serial_number',
      ...GetColumnLiteralSearchProps('modem_serial_number', lang),
    },
    {
      title: 'MAC',
      dataIndex: 'modem_mac',
      ...GetColumnLiteralSearchProps('modem_mac', lang),
    },
    {
      title: lang.handle('Modelo'),
      dataIndex: 'model_name',
      filters: modelFilter,
      onFilter: (value, record) => record.model_uuid === value,
    },
    {
      title: lang.handle('Versión'),
      dataIndex: 'version_name',
      filters: versionFilter,
      onFilter: (value, record) => record.version_uuid === value,
      sorter: (a, b) => (a.version_name || '').localeCompare(b.version_name || ''),
    },
    {
      title: lang.handle('Organización'),
      dataIndex: 'tenant_name',
      filters: tenantsFilter,
      onFilter: (value, record) => record.tenant_uuid === value,
    },
    {
      title: 'ICC',
      dataIndex: 'icc',
      ...GetColumnLiteralSearchProps('icc', lang),
    },
    {
      title: 'VPN',
      dataIndex: 'vpnLabel',
      filters: [
        { text: lang.handle('Sí'), value: 1 },
        { text: lang.handle('No'), value: 0 },
      ],
      onFilter: (value, record) => (record.vpn ? 1 : 0) === value,
    },
    {
      title: lang.handle('Estado'),
      dataIndex: 'state',
      filters: [
        { text: lang.handle('Fabricado'), value: 'Fabricado' },
        { text: lang.handle('Asignado'), value: 'Asignado' },
        { text: lang.handle('Terminado'), value: 'Terminado' },
        { text: lang.handle('Eliminado'), value: 'Eliminado' },
      ],
      onFilter: (value, record) =>
        record.state && record.state.toLowerCase() === value.toLowerCase(),
    },
    {
      title: lang.handle('Fecha fabricación'),
      dataIndex: 'dateFormated',
      defaultSortOrder: 'descend',
      sorter: (a, b) => compareDates(a.date, b.date),
      sortDirections: ['descend', 'ascend'],
    }
  ], [lang, modelFilter, versionFilter, tenantsFilter]);

  const displayedColumns = useMemo(() => {
    if (ui.selectedColumns === null || ui.selectedColumns.length === 0) return allColumns;
    return allColumns.filter(col => ui.selectedColumns.includes(col.dataIndex));
  }, [ui.selectedColumns, allColumns]);

  const changeColumns = useCallback((newColumns) => {
    if (newColumns.length === 0) {
      updateUI('selectedColumns', null);
      localStorage.removeItem(COLUMNS_STORAGE_KEY);
      return;
    }

    const keys = newColumns.map(col => col.dataIndex);
    updateUI('selectedColumns', keys);
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(keys));
  }, []);

  /**
   * Obtiene los datos que se exportarán a Excel, basándose en las filas filtradas y las columnas visibles.
   */
  const getExportData = () => {
    const source = data.filteredRows.length > 0 ? data.filteredRows : enrichedData;
    return source.map((row) => {
      const obj = {};
      displayedColumns.forEach((col) => {
        obj[col.title] = row[col.dataIndex] ?? '';
      });
      return obj;
    });
  };

  let selection = null;
  
  if (hasPrivilege('administration_delete') || hasPrivilege('administration_edit')) {
    selection = {
      type: 'radio',
      selectedRowKeys: data.selectedId ? [data.selectedId] : [],
      onChange: onRowSelectionChange,
    };
  }

  const deleteInfo = {
    willBeDeleted: [
      lang.handle('La información del router'),
      lang.handle('La suscripción M2M relacionada'),
      lang.handle('La información de la VPN (si existe)'),
    ],
    willStay: [
      lang.handle('El modem Teltonika volverá a estar disponible en stock'),
    ],
  };

  // ===== 9. RENDER =====
  return (
    <div style={{ position: 'relative' }}>
      {ui.loading && <Loading loading={ui.loading} />}
      <div className="row">
        <div className="col">
          <FilterColumns
            columns={allColumns}
            selectedColumns={ui.selectedColumns === null ? [] : displayedColumns}
            changeColumns={changeColumns}
          />
        </div>
      </div>
      <DataTable
        rowSelection={selection}
        columns={displayedColumns}
        rowKey="uuid"
        dataSource={enrichedData}
        onChange={handleTableChange}
        loading={false}
        scroll={displayedColumns.length > 5 ? { x: 'max-content' } : null}
      />
      <div className="button-panel">
        <Space>
          <Excelexport
            fileName={'routers_' + dayjs().format('YYYYMMDD_HHmmss')}
            fileData={() => [{ name: 'routers', data: getExportData() }]}
            disabled={(data.filteredRows.length || enrichedData.length) === 0}
            label={lang.handle('Exportar a Excel')}
          />

          {hasPrivilege('administration_delete') && (
            <DeleteButton
              onEvent={onDelete}
              buttonDisabled={!data.selectedId}
              willBeDeleted={deleteInfo.willBeDeleted}
              willStay={deleteInfo.willStay}
            />
          )}

          {hasPrivilege('administration_edit') && (
            <Button
              type="primary"
              icon={<FormOutlined />}
              disabled={!data.selectedId}
              onClick={onEdit}
            >
              {lang.handle('Editar')}
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
}

export default withLocale(withNavigation(RouterList));
